import React, { useState, useRef, useEffect, useMemo } from "react"

import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  List,
} from "react-virtualized";

import { LoadingWithLabel, Spinner } from "../Loading"
import { AvatarIcon } from "../Avatar"
import { InfoTooltip } from "../Tooltip"
import moment from "moment"
import { cleanMessageTemplate } from "../../container/NewChatContainer"
import "./styles.scss"
import { BsFiletypeCsv, BsFiletypeDocx, BsFiletypePdf, BsFiletypePptx, BsFiletypeXlsx } from "react-icons/bs";

const TestimonialsList = ({
  data,
  isLoading,
  isFavoriteRefetching,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  scopeId,
  handleFavoriteTestimonial,
  favoritedTestimonials,
  className = "",
}) => {
  const listRef = useRef(null)
  const [isFirstRender, setIsFirstRender] = useState(true);

  const testimonials = data

  // Usando useMemo para evitar recriar o cache em cada renderização
  const cache = useMemo(
    () =>
      new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 20,
      }),
    [scopeId, (favoritedTestimonials && isFavoriteRefetching), isLoading] // Cache é criado apenas uma vez
  );

  const loadMoreRows = () => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }

  const toggleToBottom = () => {
    if (listRef.current && testimonials.length > 0) {
      requestAnimationFrame(() => {
        const rowIndex = testimonials.length - 1
        listRef.current.scrollToRow(Math.max(rowIndex, 0))
      })
    }
  }

  const groupMessages = (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return []

    const groups = messages.reduce((acc, message) => {
      const key = `${message.id}-${message.nameSender}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push(message)
      return acc
    }, {})

    return Object.values(groups)
  }

  const groupFiles = (images, fileTypes, name) => {
    if (!Array.isArray(images) || images.length === 0) return []

    const groups = images.reduce((acc, image, index) => {
      if (!image || image === "") return acc
      const fileType = fileTypes[index]
      const key = `${image}-${name}`
      if (!acc[key]) {
        acc[key] = []
      }
      acc[key].push({ image, fileType })
      return acc
    }, {})

    return Object.values(groups)
  }

  //console.log(data?.pages[nextIndex] || 0)

  /* useEffect(() => {
    if (!isFetchingNextPage && testimonials.length > 0) {
      if (!isFirstRender) {
        toggleToBottom();
      } else {
        setIsFirstRender(false); // Após a primeira renderização, não vai chamar o toggleToBottom
      }
    }
  }, [isFetchingNextPage, testimonials, isFirstRender]); */

  const Row = ({ index, key, parent, style }) => {
    const row = testimonials[index]

    if (row?.projectId) {
      const groupedFiles = groupFiles(row?.images, row?.fileTypes, row?.name)
      const isLastGroup = (groupIndex) => groupIndex === groupedFiles.length - 1

      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
          <div style={{ ...style }} className="row-style">
            <div className="testimonials-row">
              {groupedFiles.map((group, groupIndex) => {
                return (
                  <React.Fragment key={groupIndex}>
                    {groupIndex === 0 && (
                      <div className="testimonials-user">
                        <AvatarIcon size={32} name={row?.name} backgroundColor="random" />
                        <span className="testimonials-user-name" title={row?.name}>
                          {row?.name}
                        </span>
                      </div>
                    )}

                    {group.map((item, index) => {
                      return (
                        <div
                          className={`testimonials-bubble-container ${groupIndex === 0 ? "only-first-border" : "all-items-border"
                            }`}>
                          <div className="testimonials-bubble-item">
                            <span className="testimonials-bubble-item__project">{item?.url}</span>
                            <p className="testimonials-bubble-item__message">
                              {textVerification(
                                item?.fileType
                                  ? {
                                    ...item,
                                    attachment: {
                                      remotePath: item?.image,
                                      contentType: item.fileType,
                                    },
                                  }
                                  : item,
                              )}
                            </p>
                          </div>
                        </div>
                      )
                    })}
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        </CellMeasurer>
      )
    } else {
      const groupedMessages = groupMessages(row?.messageIds)
      const isLastGroup = (groupIndex) => groupIndex === groupedMessages.length - 1

      return (
        <CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
          <div style={{ ...style }} className="row-style">
            <div className="testimonials-row">
              {groupedMessages.map((group, groupIndex) => {
                const showDateAndTooltip = isLastGroup(groupIndex) || groupedMessages.length === 1
                const item = group[0]

                return (
                  <React.Fragment key={groupIndex}>
                    {groupIndex === 0 && (
                      <div className="testimonials-user">
                        <AvatarIcon size={32} name={item.nameSender} backgroundColor="random" />
                        <span className="testimonials-user-name" title={item.nameSender}>
                          {item.nameSender}
                        </span>
                      </div>
                    )}

                    <div
                      className={`testimonials-bubble-container ${groupIndex === 0 ? "only-first-border" : "all-items-border"
                        }`}>
                      <div className="testimonials-bubble-item">
                        <span className="testimonials-bubble-item__project">{item.project}</span>
                        <p className="testimonials-bubble-item__message">
                          {group.some((item) => item.attachment) ? (
                            <div className="attachment">
                              {group.map((item) => item.attachment && textVerification(item))}
                            </div>
                          ) : (
                            textVerification(group.map((item) => item.content).join(" "))
                          )}
                        </p>
                      </div>
                    </div>

                    {showDateAndTooltip && (
                      <div className="testimonials-bottom">
                        <span className="testimonials-bottom__date">
                          {moment(new Date(item.date.seconds * 1000)).format("DD/MM/YYYY")}
                        </span>
                        <InfoTooltip
                          info={`${row?.value === "Sim" ? "Desmarcar" : "Marcar"}`}
                          trigger="hover">
                          <div
                            className={`testimonials-bottom__${row?.value === "Sim" ? "favorited" : "unfavorited"
                              }`}
                            role="button"
                            title=""
                            onClick={() =>
                              handleFavoriteTestimonial(
                                {
                                  ...row,
                                  value: row?.value === "Sim" ? "Não" : "Sim",
                                },
                                index,
                              )
                            }>
                            {row?.value === "Sim" ? (
                              <div className="favorite-testimonial" />
                            ) : (
                              <div className="unfavorite-testimonial" />
                            )}
                          </div>
                        </InfoTooltip>
                      </div>
                    )}
                  </React.Fragment>
                )
              })}
            </div>
          </div>
        </CellMeasurer>
      )
    }
  }


  return (
    <div className={`testimonials-list-view${className ? ` ${className}` : ""}`}>
      <AutoSizer className="testimonials-scroller">
        {({ width, height }) => (
          <>
            {isLoading || (favoritedTestimonials && isFavoriteRefetching) ? (
              <div className="loading-content" style={{ height, width }}>
                <Spinner color="primary" />
              </div>
            ) : testimonials?.length > 0 ? (
              <List
                ref={listRef}
                deferredMeasurementCache={cache}
                height={height - 40}
                rowCount={testimonials?.length}
                rowHeight={cache.rowHeight}
                overscanRowCount={10}
                rowRenderer={Row}
                width={width}
                innerElementType="ul"
                className="testimonials-list-view-scroll"
              />
            ) : (
              <div className="channels-empty" style={{ height, width }}>
                <h3>{favoritedTestimonials ? "Não há destaques salvos" : "Não há destaques"}</h3>
              </div>
            )}
            {hasNextPage && !favoritedTestimonials ? (
              <div className="load-more-container">
                <button
                  onClick={loadMoreRows}
                  disabled={isFetchingNextPage}
                  className="load-more-btn">
                  {isFetchingNextPage ? (
                    <div className="load-more-btn-spinner">
                      <Spinner size="small" color="primary" /> Carregando...
                    </div>
                  ) : (
                    <div className="load-more-btn-mess">
                      <i class="fi fi-br-plus"></i> Carregar mais itens
                    </div>
                  )}
                </button>
              </div>
            ) : (
              <div className="load-more-container">
                <button disabled={true} className="load-more-btn">
                  <div className="load-more-btn-mess">
                    {isLoading ? (
                      <div className="load-more-btn-spinner">
                        <Spinner size="small" color="primary" /> Carregando...
                      </div>
                    ) : (
                      <>{testimonials?.length || 0} itens carregados</>
                    )}
                  </div>
                </button>
              </div>
            )}
          </>
        )}
      </AutoSizer>
    </div>
  )
}

export default TestimonialsList

const MessageDocComponent = ({ item }) => {
  const handleAttachmentClick = () => {
    if (item.attachment) {
      window.open(item.attachment.remotePath)
    }
  }

  const getFileIcon = () => {
    if (item.attachment) {
      if (item.attachment.contentType.includes("pdf")) {
        return <BsFiletypePdf size={20} />
      } else if (item.attachment.contentType.includes("document")) {
        return <BsFiletypeDocx size={20} />
      } else if (item.attachment.contentType.includes("sheet")) {
        return <BsFiletypeXlsx size={20} />
      } else if (item.attachment.contentType.includes("presentation")) {
        return <BsFiletypePptx size={20} />
      } else if (item.attachment.contentType.includes("csv")) {
        return <BsFiletypeCsv size={20} />
      }
    }
    return null
  }

  const isAttachmentValid =
    !item.attachment ||
    (!item.attachment.contentType.includes("csv") &&
      !item.attachment.contentType.includes("presentation") &&
      !item.attachment.contentType.includes("sheet") &&
      !item.attachment.contentType.includes("document") &&
      !item.attachment.contentType.includes("pdf"))

  return (
    <div
      className={`message-doc ${isAttachmentValid && "invalid-doc-file"}`}
      onClick={handleAttachmentClick}
      title="Documento">
      {getFileIcon()}
      {isAttachmentValid ? (
        <span className="message-doc-name" title={item.message}>
          {item.message}
        </span>
      ) : (
        <span className="message-doc-name" title={item.attachment?.name}>
          {item.attachment?.name}
        </span>
      )}
    </div>
  )
}

const renderImage = (item) => (
  <img
    src={item.attachment.remotePath}
    className="img-chat"
    width={300}
    height={400}
    loading="lazy"
  />
)

const renderAudio = (item) => (
  <audio
    id="audio"
    controls
    src={item.attachment.remotePath}
    width={300}
    height={100}
    className="audio-component"
    loading="lazy"></audio>
)

const renderVideo = (item) => (
  <video
    src={`${item.attachment?.remotePath}&quality=360p`}
    width={300}
    height={300}
    controls
    id="video"
  />
)

const textVerification = (item) => {
  if (!item) return null

  const contentType = item?.attachment?.contentType || item?.contentType

  if (item?.attachment?.localPath?.includes('https://api.twilio.com/') && contentType.includes('video')) {
    return (
      <p className={`message`}>
        A mídia não é suportada pela plataforma
      </p>
    )
  }

  if (contentType) {
    const type = contentType?.split("/")[0]

    switch (type) {
      case "image":
        return renderImage(item)
      case "audio":
        return renderAudio(item)
      case "video":
        return renderVideo(item)
      default:
        return <MessageDocComponent item={item} />
    }
  } else {
    const cleanedText = cleanMessageTemplate(item)
    return (
      <p title={`${cleanedText}`} className={`message`}>
        {cleanedText}
      </p>
    )
  }
}